import { Dialog, Transition } from "@headlessui/react";
import React, { FC, Fragment, memo, ReactNode } from "react";
import clsx from "clsx";

import { IconButton } from "@/components/atoms/buttons/IconButton";

import CloseIcon from "/public/icon/close.svg";

export enum Sizes {
  S = "S",
  M = "M",
  L = "L",
  XL = "XL",
  XXXXL = "4XL",
  XXXXXL = "5XL",
  MAX_CONTENT = "max-content",
}

// モーダルサイズ定義
const ModifierClassNames = {
  [Sizes.S]: "max-w-sm",
  [Sizes.M]: "max-w-[380px]",
  [Sizes.L]: "max-w-lg",
  [Sizes.XL]: "max-w-[680px]",
  [Sizes.XXXXL]: "max-w-[800px]",
  [Sizes.XXXXXL]: "max-w-5xl",
  [Sizes.MAX_CONTENT]: "max-content",
};

type SpSize = "M" | "full";

interface Props {
  size?: Sizes;
  spSize?: SpSize;
  isOpen: boolean;
  handleClose: () => void;
  children: ReactNode;
  containerClassName?: string;
  className?: string;
}

interface ModalHeadProps {
  title: string | ReactNode;
  onClickClose: () => void;
  showCloseIcon?: boolean;
  className?: string;
}

interface ModalFootProps {
  children: ReactNode;
  className?: string;
}

export const ModalHead: FC<ModalHeadProps> = ({
  title,
  onClickClose,
  showCloseIcon = true,
  className = "",
}: ModalHeadProps) => {
  return (
    <div className={`flex justify-between items-center py-2 pr-3 pl-5 border-b md:py-4 md:pr-5 ${className}`}>
      <Dialog.Title
        as="h3"
        className="text-[22px] font-bold leading-6 md:text-xl"
      >
        {title}
      </Dialog.Title>
      {showCloseIcon && (
        <IconButton
          icon={<CloseIcon className="w-8 h-8" />}
          className="bg-white rounded-full border-0"
          onClick={onClickClose}
        />
      )}
    </div>
  );
};

export const ModalFoot: FC<ModalFootProps> = ({
  children,
  className = "",
}: ModalFootProps) => {
  return (
    <div className={`flex justify-end items-center p-4 border-t ${className}`}>
      {children}
    </div>
  );
};

const ModalBase: FC<Props> = ({
  size = Sizes.M,
  spSize = "M",
  isOpen,
  handleClose,
  children,
  containerClassName,
  className,
}: Props) => {
  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" />
          </Transition.Child>

          <div className="overflow-y-auto fixed inset-0">
            <div
              className={clsx(
                "flex justify-center items-center min-h-full text-center",
                spSize === "full" ? "md:p-4" : "p-4",
                containerClassName,
              )}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className={`w-full text-left align-middle bg-white drop-shadow-[0_1px_5px_rgba(0,0,0,0.15)] transition-all ${
                    ModifierClassNames[size]
                  } ${
                    spSize === "full"
                      ? "min-h-screen md:min-h-0 md:rounded-lg"
                      : "rounded-lg"
                  } ${className ?? ""}`}
                >
                  {children}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export const Modal = memo(ModalBase);
